
import { defineComponent, ref, shallowRef, onMounted, computed } from "vue";
import { LocationAPI } from "@/services/api/Location.service";
import { ILookup } from "@/interfaces/iLookup";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import VZSideDrawer from "@/components/content/VZSideDrawer.vue";
import { FormatMethods, getMaxLengthErrorMessage } from "@/helpers/data-format";
import { usePagination } from "@/helpers/pagination";
import LocationDetail from "@/views/location/views/LocationDetail.vue";
import LocationSite from "@/views/location/views/LocationSite.vue";
import LocationDevice from "@/views/location/views/LocationDevice.vue";
import LocationActivity from "@/views/location/views/LocationActivity.vue";
import LocationDetailView from "@/views/location/views/LocationDetailView.vue";
import { ITabConfig } from "@/interfaces/iTabConfig";
import { IBulkDelete } from "@/interfaces/iPageModel";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { TranslationService } from "@/core/services/TranslationService";
import { IAddEditFormModel } from "@/interfaces/iAddEditFormModel";
import { DrawerComponent } from "@/assets/ts/components";
import VZDeleteModal from "@/components/content/VZDeleteModal.vue";
import * as Yup from "yup";
import { useToast } from "vue-toastification";
import {
  lookupTableBulkDelete,
  locationTableFilters,
  locationTableHeaders,
} from "./dataTableOptions";
import { hasPermission } from "@/core/services/permission.service";
import { ILocation } from "@/interfaces/iLocation";
import { getLookups } from "@/services/api/Lookup.service";
import toast from "bootstrap/js/dist/toast";
import loading from "element-plus/es/components/loading";
import ActivityModal from "@/views/user/modal/ActivityModal.vue";
export default defineComponent({
  name: "location-index",
  // eslint-disable-next-line vue/no-unused-components
  components: {
    Datatable,
    VZSideDrawer,
    // eslint-disable-next-line vue/no-unused-components
    LocationDetail,
    // eslint-disable-next-line vue/no-unused-components
    LocationSite,
    // eslint-disable-next-line vue/no-unused-components
    LocationDetailView,
    // eslint-disable-next-line vue/no-unused-components
    LocationDevice,
    // eslint-disable-next-line vue/no-unused-components
    LocationActivity,
    VZDeleteModal,
    ActivityModal,
  },
  setup() {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    const datatable = ref<null | HTMLFormElement>(null);
    const toast = useToast();
    const addMessage = t("pages.location.locationAddedMessage");
    const editMessage = t("pages.location.locationUpdatedMessage");
    const { page, total, rowsPerPage } = usePagination();
    const loading = ref(false);
    const showDeleteDialog = ref<any>(false);
    const defaultFilter = ref({ status: "ACTIVE" });
    const showSearch = true;
    const sideDrawerTitle = ref<string>("");
    const sideDrawerIcon = ref<string>("");
    // const options = ref<{ label: string; value: string }[]>([]);
    let options: { label: any; value: string }[] = [];

    const openDrawer = ref<boolean>(false);
    const tableData = ref<ILocation[]>([]);
    const selectedForAction = ref<Partial<ILocation>>({});
    const bulkDelete = ref<IBulkDelete>(lookupTableBulkDelete());
    const tableHeader = ref(locationTableHeaders());
    const tableFilter = ref(locationTableFilters());
    const tabs = shallowRef<ITabConfig[]>([
      {
        title: t("pages.location.locationDetail"),
        name: "location.detail",
        icon: "bi bi-card-text me-2 fs-4 text-primary",
        component: LocationDetail,
        props: {
          data: {},
        },
      },
      {
        title: t("common.sites"),
        name: "location.site",
        icon: "bi bi-diagram-3 me-2 fs-4 text-primary",
        component: LocationSite,
        props: {
          data: {},
          openDrawer: openDrawer,
        },
      },
      {
        title: t("common.devices"),
        name: "location.device",
        icon: "bi bi-cpu me-2 fs-4 text-primary",
        component: LocationDevice,
        props: {
          data: {},
          openDrawer: openDrawer,
        },
      },
      {
        title: t("common.activity"),
        name: "location.activity",
        icon: "bi bi-signpost-2 me-2 fs-4 text-primary",
        component: LocationActivity,
        props: {
          data: {},
        },
      },
    ]);
    let formData = ref<IAddEditFormModel>({
      header: "Location",
      edit: false,
      data: null,
      defaultValues: {
        status: "Active",
      },
      label: [
        { addHeader: t("pages.location.addLocation") },
        { editHeader: t("pages.location.editLocation") },
      ],
      validationSchema: Yup.object({
        name: Yup.string()
          .strict(true)
          .max(30, getMaxLengthErrorMessage(t("common.name"), 30))
          .required(t("common.name") + " " + t("common.isRequired"))
          .matches(/\S/, t("common.name") + " " + t("common.isRequired")),
        siteId: Yup.array()
          .nullable()
          .min(1, t("common.sites") + " " + t("common.isRequired"))
          .required(t("common.sites") + " " + t("common.isRequired")),
        startDate: Yup.date()
          .nullable()
          .required(t("common.startDate") + " " + t("common.isRequired")),
        endDate: Yup.date()
          .nullable()
          .min(Yup.ref("startDate"), "End date can't be before Start date"),
        status: Yup.string().required(
          t("common.status") + " " + t("common.isRequired")
        ),
      }),
      model: [
        {
          name: "name",
          label: t("common.name"),
          type: "text",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "",
        },
        {
          name: "siteId",
          label: t("common.sites"),
          type: "list",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Please select branch",
          // options: options.value,
          options: options,
          multiSelect: true,
        },
        {
          name: "startDate",
          label: t("common.startDate"),
          type: "date",
          required: true,
          format: "DD-MM-YYYY",
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Select date",
        },
        {
          name: "endDate",
          label: t("common.endDate"),
          type: "date",
          required: false,
          format: "DD-MM-YYYY",
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Select date",
        },
        {
          name: "status",
          label: t("common.status"),
          type: "text",
          required: true,
          showOnlyInEdit: false,
          disabled: true,
          placeholder: "Please select status",
          options: [
            {
              label: t("common.active"),
              value: "Active",
            },
            {
              label: t("common.inactive"),
              value: "Inactive",
            },
          ],
        },
      ],
    });
    let permissionModel = ref<any>({
      create: "VYZOR:BUSINESS:LOCATIONS:CREATE",
      delete: "VYZOR:BUSINESS:LOCATIONS:DELETE",
      update: "VYZOR:BUSINESS:LOCATIONS:UPDATE",
    });
    const localPreference = ref({});
    const format = computed(() => {
      return FormatMethods;
    });
    const tableRowClick = (item) => {
      tabs.value = tabs.value.map((tab) => {
        return { ...tab, props: { ...tab.props, data: item } };
      });
      sideDrawerTitle.value = item.name;
      sideDrawerIcon.value = "bi bi-geo-alt";
      openDrawer.value = true;
      DrawerComponent?.getInstance("vz__drawer")?.toggle();
    };
    onMounted(async () => {
      setCurrentPageBreadcrumbs(t("pages.location.title"), [
        "Home",
        "Locations",
      ]);
      loading.value = true;
      // await queryUpdate({
      //   ...defaultFilter.value,
      //   page: page.value - 1,
      //   size: rowsPerPage.value,
      // });
      await getLookups("site/filter/SITES")
        .then((res) => {
          res.map((data) => {
            options.push({
              label: data.text,
              value: data.value,
            });
          });
        })
        .catch((err) => {
          console.error(err);
        });

      bulkDelete.value.entityLabel = "locations";
      var drawerElement = document.getElementById("vz__drawer");
      drawerElement?.addEventListener("transitionend", function (event) {
        if (event?.propertyName === "transform") {
          const targetElement = event.target as HTMLElement;
          if (targetElement.className === "drawer drawer-end") {
            openDrawer.value = false;
            // queryUpdate({
            //   ...localPreference.value,
            //   page: page.value - 1,
            //   size: rowsPerPage.value,
            // });
          }
        }
      });
    });

    const onBulkDelete = async (ids) => {
      showDeleteDialog.value = false;
      await LocationAPI.getInstance()
        .deleteBulkLocations(ids)
        .then(() => {
          toast.success("Selected location(s) have been deleted successfully.");
          refreshList();
        })
        .catch(() => {
          //
        })
        .finally(() => {
          loading.value = false;
        });
    };

    const queryUpdate = async (params?: any) => {
      try {
        loading.value = true;
        if (!params || !params.sort) {
          params = {
            ...params,
            sort: "createdDate,desc",
            startDate: params?.startDate === null ? [] : params?.startDate,
          };
        }
        localPreference.value = { ...params };
        const data = await LocationAPI.getInstance().getLocationList({
          ...params,
        });
        tableData.value = [...data.content];
        total.value = data.totalElements;
        rowsPerPage.value = data.size;
        page.value = ++data.number;
      } finally {
        loading.value = false;
      }
    };

    const editLocation = (item: any) => {
      if (datatable.value) datatable.value.openAddEditModal = true;

      const siteIds = item.siteLocationModels?.flatMap(
        (site: any) => site.siteId
      );

      const siteIdsAsString = siteIds?.map(String);

      formData.value.data = {
        ...item,
        siteId: siteIdsAsString || null,
      };
      formData.value.edit = true;
    };

    const submit = async (reqData) => {
      loading.value = true;

      const siteIdArray = reqData.siteId || [];
      const siteIdString = siteIdArray.join(",");

      reqData = formData.value.edit
        ? reqData
        : { ...reqData, deleted: "false" };

      let message = formData.value.edit ? editMessage : addMessage;
      delete reqData.siteId;

      await LocationAPI.getInstance()
        .addLocation(reqData, formData.value.edit, siteIdString)
        .then(() => {
          toast.success(message);
          queryUpdate({
            ...localPreference.value,
            page: page.value - 1,
            size: rowsPerPage.value,
          });

          datatable.value?.VZFilter?.getLookupData();
        })
        .catch(() => {
          //
        })
        .finally(() => {
          loading.value = false;
        });
    };
    const refreshList = () => {
      showDeleteDialog.value = false;
      queryUpdate({ ...localPreference.value });
    };
    return {
      bulkDelete,
      format,
      formData,
      tabs,
      tableData,
      tableHeader,
      sideDrawerTitle,
      sideDrawerIcon,
      showSearch,
      openDrawer,
      page,
      total,
      rowsPerPage,
      defaultFilter,
      loading,
      selectedForAction,
      editLocation,
      tableRowClick,
      queryUpdate,
      submit,
      t,
      refreshList,
      permissionModel,
      hasPermission,
      datatable,
      showDeleteDialog,
      onBulkDelete,
      tableFilter,
    };
  },
});
